import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, filter, map, switchMap, tap } from 'rxjs';

import { LoadingState } from '@personal-area/global/state';
import { Role } from '@personal-area/global/users/models';
import { AuthService } from '@personal-area/global/authorization/services';
import {
  Account,
  AccountCertificate,
  AccountPermissions,
} from '@personal-area/global/accounts/models';

import { calculateBalanceFixed } from '../../utils/balance-utils';
import { AccountActions } from '../actions/account';
import { AccountState, filterAvailableForTrading } from '../reducers/account';
import {
  getAccountsLoadingState,
  getAllAccounts,
  getCertificate,
  getSelectedAccountID,
  getSelectedAction,
} from '../selectors/account';

@Injectable({ providedIn: 'root' })
export class AccountsFacade {
  public accounts$ = this._store.select(getAllAccounts).pipe(
    tap(({ loadingState }) => {
      if (
        loadingState === LoadingState.initial &&
        this._authService.isLoggedIn
      ) {
        this.getAccounts();
      }
    }),
    filter(({ loadingState }) =>
      [LoadingState.loaded, LoadingState.failed].includes(loadingState),
    ),
  );
  public getAccountsAvailableForTrading$ = this.accounts$.pipe(
    map(({ accounts }) => filterAvailableForTrading(accounts)),
  );
  public getAccountsAvailableForTradingInDescOrder$ =
    this.getAccountsAvailableForTrading$.pipe(
      map((accounts) => [...accounts].reverse()),
    );
  public getAccountsForTransactionHistory$ = this.accounts$.pipe(
    map(({ accounts }) =>
      accounts.filter(
        (account) =>
          account.type === Role.Exchanger || (account.login && !account.isDemo),
      ),
    ),
  );
  public getAccountsForTransactionHistoryInDescOrder$ =
    this.getAccountsForTransactionHistory$.pipe(
      map((accounts) => [...accounts].reverse()),
    );
  public getSelectedAccount$ = this._store.select(getSelectedAccountID).pipe(
    filter((accountID) => !!accountID && accountID !== 'all'),
    switchMap((accountID) =>
      this.accounts$.pipe(
        map(({ accounts }) => accounts.find(({ id }) => id === accountID)),
      ),
    ),
  );
  public getSelectedAction$ = this._store.select(getSelectedAction);
  public getAccountsLoadingState$ = this._store.select(getAccountsLoadingState);
  public getBonusAccounts$ = this.accounts$.pipe(
    map(({ accounts }) => accounts.filter(({ isBonus }) => isBonus)),
  );

  constructor(
    private _store: Store<AccountState>,
    private _authService: AuthService,
  ) {}

  public getAccount$(accountId: number): Observable<Account> {
    return this.accounts$.pipe(
      map(({ accounts }) => accounts.find(({ id }) => id === accountId)),
      filter(Boolean),
    );
  }

  public getCertificate$(
    accountID: number,
  ): Observable<AccountCertificate | undefined> {
    return this._store.select(getCertificate(accountID));
  }

  public getAccounts(): void {
    this._store.dispatch(AccountActions.getAccounts(this._authService.userId));
  }

  public getAccount(accountId: number): void {
    this._store.dispatch(AccountActions.getAccount({ accountId }));
  }

  public select(accountId: number): void {
    this._store.dispatch(AccountActions.selectAccount(accountId));
  }

  public selectPartnerAcc(): void {
    this._store.dispatch(AccountActions.selectPartnerAccount());
  }

  public selectExchangerAcc(): void {
    this._store.dispatch(AccountActions.selectExchangerAccount());
  }

  public selectFirstAvailable(): void {
    this._store.dispatch(AccountActions.selectFirstAvailableAccount());
  }

  public selectAction(action: AccountPermissions): void {
    this._store.dispatch(AccountActions.selectAction(action));
  }

  public selectPrimary(accountId: number): void {
    this._store.dispatch(AccountActions.selectPrimaryAccount(accountId));
  }

  public updateAccount(id: number, account: Partial<Account>): void {
    this._store.dispatch(
      AccountActions.updateAccount({
        update: {
          id,
          changes: this._computeBalanceFixed(account),
        },
      }),
    );
  }

  public saveCertificates(id: number, certificate: AccountCertificate): void {
    this._store.dispatch(AccountActions.saveCertificate({ id, certificate }));
  }

  public removeCertificate(id: number): void {
    this._store.dispatch(AccountActions.removeCertificate({ id }));
  }

  public createPartnerAccount(): void {
    this._store.dispatch(AccountActions.createPartnerAccount());
  }

  private _computeBalanceFixed(account: Partial<Account>): Partial<Account> {
    const { balance, credit, extras, type } = account;

    if (
      balance !== undefined &&
      credit !== undefined &&
      extras?.equity !== undefined
    ) {
      return {
        ...account,
        balanceFixed: calculateBalanceFixed(
          type,
          balance,
          credit,
          extras.equity,
        ),
      };
    }

    return account;
  }
}
